import useTheme from '@mui/material/styles/useTheme';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { type Location, useLocation, useNavigate, useParams } from 'react-router-dom';
import { setAllVehiclesState, setLoginToken, setVertrage } from '../_flux/Actions';
import { getVertrageFirma, switchFirma } from '../_tools/AxiosFirma';
import { getSchnittstelle } from '../_tools/AxiosImport';
import { getLoginSession, log, loginOverUWE } from '../_tools/AxiosVars';
import BoxMaterial from '../_tools/BoxMaterial';
import Loading from '../_tools/Loading';
import Meta from '../_tools/Meta';
import { type Session, clearSession, getRoute, getSessionName, setSession, setVertretungSeit, setVertretungUser, setupCrisp } from '../_tools/Session';
import Toast from '../_tools/Toast';
import { betterUseNavigate, checkUndefinedNullOrEmpty, parseJson } from '../_tools/Tools';
import { convertVertrage, getVertrag } from '../_tools/Vertrag';
import { RuntimeError } from '../errors/RuntimeError';
import LandingPage from '../layouts/LandingPage';
import { PATH_AHDCARS } from '../paths';
import { type CustomTheme } from '../provider/ThemeProvider';
import { LEAD } from '../vars';
import { type UserSettings, defaultConfig } from './EnsureLoggedInContainer';

const LoginRedirect = () => {
    const dispatch = useDispatch();

    const loc: Location & { state: { [index: string]: string | number | boolean } } = useLocation();
    const params = useParams();
    const match = { params };
    const navigate = useNavigate();
    const theme: CustomTheme = useTheme();

    const [loading, setLoading] = useState<number>(0);

    const loadingTexts = [
        loc?.state?.changeFirma ? 'Firma wird gewechselt...' : 'AHD Cars wird geladen...',
        'Schon gewusst?\nNeue Funktionen können Sie im News-Bereich des Dashboards sehen...',
        'Das Laden dauert länger als erwartet...',
        'Schon gewusst?\nSie können uns jederzeit über unser Chat-Tool eine Nachricht senden...',
    ];

    const setLogin = useCallback(
        async (d: Session & { conf?: string }) => {
            try {
                const url = !checkUndefinedNullOrEmpty(loc?.state?.redir) ? loc?.state?.redir : `/${PATH_AHDCARS.dashboard.root}`;

                await getVertrageFirma(d.gIdFirma, { customToken: d.token, customTokenHeaderName: d.header }).then(({ data: dataVertrage }) => {
                    const vertrage = convertVertrage(dataVertrage, d?.gIdFirma);
                    if (LEAD && !getVertrag('uwe', vertrage)) {
                        dispatch(setLoginToken(''));
                        setVertretungSeit(undefined);
                        setVertretungUser(undefined);
                        clearSession();
                        betterUseNavigate(navigate, loc?.state, { replace: true, state: { showMissingContract: true } }, false);
                    } else {
                        const newSession: Session = d;
                        if (typeof d.conf === 'string') {
                            const newConf = parseJson<UserSettings>(d.conf);
                            if (!checkUndefinedNullOrEmpty(newConf)) {
                                newSession.conf = newConf;
                            } else {
                                newSession.conf = defaultConfig(theme, vertrage);
                                log(
                                    newSession.gIdFirma || 0,
                                    newSession.gIdUser || 0,
                                    2,
                                    JSON.stringify({
                                        origConf: d.conf,
                                        message: 'Ihre Benutzereinstellungen konnten nicht richtig abgerufen werden und werden deshalb bei der nächsten Änderung zurückgesetzt!',
                                    })
                                ).then(() => {
                                    Toast.addError({
                                        key: 'LoginController_setLogin',
                                        primary: 'Fehler beim Abrufen der Benutzereinstellungen',
                                        secondary: 'Ihre Benutzereinstellungen konnten nicht richtig abgerufen werden und werden deshalb bei der nächsten Änderung zurückgesetzt!',
                                    });
                                });
                            }
                        }

                        newSession.allowDAT = false;
                        setSession(newSession);

                        getSchnittstelle(newSession.gIdFirma).then(({ data }) => {
                            for (let i = 0; i < data.length; i++) {
                                if (!newSession.allowDAT && (newSession.gIdFirma < 10000 || data[i]?.idQuelle === 68)) {
                                    newSession.allowDAT = true;
                                    setSession(newSession);
                                    break;
                                }
                            }
                        });

                        setupCrisp(newSession);
                        dispatch(setLoginToken(newSession.token));
                        dispatch(setVertrage(vertrage));
                        betterUseNavigate(navigate, loc?.state, { replace: true, pathname: url }, false);
                    }
                });
            } catch (err: any) {
                console.error(err);
            }
        },
        [dispatch, loc?.state, navigate, theme]
    );

    useEffect(
        function () {
            try {
                let timerIndex = 0;
                const timer = setInterval(() => {
                    if (timerIndex === loadingTexts.length - 1) {
                        timerIndex = 0;
                        setLoading(0);
                    } else {
                        timerIndex++;
                        setLoading(timerIndex);
                    }
                }, 5000);

                if (loc?.state?.changeFirma) {
                    switchFirma(loc?.state?.changeFirma).then(({ data }) => {
                        dispatch(setAllVehiclesState(undefined));
                        setLogin(data);
                    });
                    return function cleanup() {
                        clearInterval(timer);
                    };
                }

                // Login über UWE-X
                if (loc?.state?.enc) {
                    loginOverUWE(loc?.state?.enc)
                        .then(({ data }) => {
                            setLogin(data);
                        })
                        .catch(() => {
                            Toast.addError({ key: 'LoginRedirect_useEffect', primary: 'Anmeldung fehlgeschlagen', secondary: 'Bitte melden Sie sich mit Ihren Zugangsdaten an!' });
                            betterUseNavigate(navigate, loc?.state, { replace: false, pathname: '/login' }, false);
                        });
                    return function cleanup() {
                        clearInterval(timer);
                    };
                }

                // aktuelle Session beim Server abfragen
                if (!checkUndefinedNullOrEmpty(match?.params?.token)) {
                    getLoginSession(match?.params?.token).then(({ data }) => {
                        setLogin(data);
                    });
                    return function cleanup() {
                        clearInterval(timer);
                    };
                }

                const rawData = window.sessionStorage.getItem(getSessionName());
                const data = !checkUndefinedNullOrEmpty(rawData) ? JSON.parse(rawData) : undefined;
                if (!checkUndefinedNullOrEmpty(data?.token)) {
                    setLogin(data);
                    return function cleanup() {
                        clearInterval(timer);
                    };
                }

                const url = getRoute(
                    !checkUndefinedNullOrEmpty(loc?.state?.redir)
                        ? loc?.state?.redir
                        : !checkUndefinedNullOrEmpty(match?.params?.redirect)
                          ? match?.params?.redirect
                          : `/${PATH_AHDCARS.dashboard.root}`
                );
                if (!['/', '/dashboard'].includes(url)) {
                    Toast.addWarning({ key: 'LoginRedirect_useEffect', primary: 'Bitte anmelden', secondary: 'Ohne Anmeldung kann diese Seite nicht angezeigt werden!' });
                }
                betterUseNavigate(navigate, loc?.state, { replace: true, pathname: '/login', state: { redir: url } }, false);
                return function cleanup() {
                    clearInterval(timer);
                };
            } catch (err) {
                throw new RuntimeError(err);
            }
        },
        [dispatch, loadingTexts.length, loc?.state, match?.params?.redirect, match?.params?.token, navigate, setLogin]
    );

    return (
        <>
            <Meta title="Lade..." />
            <LandingPage>
                <BoxMaterial>
                    <Loading>{loadingTexts[loading]}</Loading>
                </BoxMaterial>
            </LandingPage>
        </>
    );
};

export default LoginRedirect;
